export const ADMIN_PROPERTIES_PAGE_SIZE = 25;

export const ADMIN_PROPERTY_LOOKUP_TYPES = [
  'email',
  'ownerName',
  'address',
  'city',
  'postalCode',
  'units',
  'createdAt',
];

function clampPageSize(pageSize) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : ADMIN_PROPERTIES_PAGE_SIZE;
}

function optionalTrim(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * Browse request for listAdminProperties.
 * @param {{ pageSize?: number, pageToken?: string | null }} [params]
 */
export function buildPropertiesBrowseRequest({
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    mode: 'browse',
    pageSize: clampPageSize(pageSize),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * Typed lookup for listAdminProperties. One lookupType at a time.
 * @param {{
 *   lookupType: string,
 *   email?: string,
 *   ownerName?: string,
 *   address?: string,
 *   city?: string,
 *   postalCode?: string,
 *   unitsMin?: number | string | null,
 *   unitsMax?: number | string | null,
 *   createdAfter?: string,
 *   createdBefore?: string,
 *   pageSize?: number,
 *   pageToken?: string | null,
 * }} params
 */
export function buildPropertyLookupRequest({
  lookupType,
  email,
  ownerName,
  address,
  city,
  postalCode,
  unitsMin,
  unitsMax,
  createdAfter,
  createdBefore,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  const type = optionalTrim(lookupType);
  const request = {
    mode: 'lookup',
    lookupType: type,
    pageSize: clampPageSize(pageSize),
  };

  if (type === 'email') request.email = optionalTrim(email);
  if (type === 'ownerName') request.ownerName = optionalTrim(ownerName);
  if (type === 'address') request.address = optionalTrim(address);
  if (type === 'city') request.city = optionalTrim(city);
  if (type === 'postalCode') request.postalCode = optionalTrim(postalCode);
  if (type === 'units') {
    if (unitsMin !== '' && unitsMin != null) request.unitsMin = Number(unitsMin);
    if (unitsMax !== '' && unitsMax != null) request.unitsMax = Number(unitsMax);
  }
  if (type === 'createdAt') {
    const after = optionalTrim(createdAfter);
    const before = optionalTrim(createdBefore);
    if (after) request.createdAfter = after;
    if (before) request.createdBefore = before;
  }
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

export function buildOwnerEmailLookupRequest({
  email,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'email', email, pageSize, pageToken });
}

export function buildOwnerNameLookupRequest({
  ownerName,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'ownerName', ownerName, pageSize, pageToken });
}

export function buildAddressLookupRequest({
  address,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'address', address, pageSize, pageToken });
}

export function buildCityLookupRequest({
  city,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'city', city, pageSize, pageToken });
}

export function buildPostalCodeLookupRequest({
  postalCode,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'postalCode', postalCode, pageSize, pageToken });
}

export function buildUnitsLookupRequest({
  unitsMin,
  unitsMax,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({ lookupType: 'units', unitsMin, unitsMax, pageSize, pageToken });
}

export function buildCreatedAtLookupRequest({
  createdAfter,
  createdBefore,
  pageSize = ADMIN_PROPERTIES_PAGE_SIZE,
  pageToken,
} = {}) {
  return buildPropertyLookupRequest({
    lookupType: 'createdAt',
    createdAfter,
    createdBefore,
    pageSize,
    pageToken,
  });
}
